import bridge from 'libs/bridge';

const isDev = process.env.NODE_ENV !== 'production';

interface PlainObjectOf<T> {
    [name: string]: T;
}

interface CustomEventParams {
    event_name: string;
    metrics: PlainObjectOf<number>;
    tags: PlainObjectOf<string>;
}

interface CustomLogParams {
    value: string;
    tags: PlainObjectOf<string>;
}

// Extend slardar params with AppInfo
async function extendSlardarParamsWithAppInfo<T>(params: any): Promise<T> {
    try {
        const appInfo: any = await bridge.call('appInfo', {}, { timeout: 1000 });

        if (params.tags && appInfo) {
            params.tags = {
                ...params.tags,
                aid: appInfo.aid || '0',
                did: appInfo.device_id || '0',
                appName: appInfo.appName || '',
                appVersion: appInfo.appVersion || '',
                netType: appInfo.netType || ''
            };
        }

        return params;
    } catch (e) {
        return params;
    }
}

/**
 * Slardar 自定义打点事件指标上报
 * See: https://slardar.bytedance.net/docs/115/150/custom_analyze/
 * @param eventName
 * @param metrics
 * @param tags
 */
export function event(
    eventName: string,
    metrics: PlainObjectOf<number>,
    tags?: PlainObjectOf<string>
): void {
    const params: CustomEventParams = {
        event_name: eventName,
        metrics,
        tags: tags || {}
    };

    extendSlardarParamsWithAppInfo(params)
        .then((params) => {
            if (isDev) return console.log('[Slardar Event]: ', params);

            window.Slardar && window.Slardar('emit', 'custom', params);
        })
        .catch(() => {});
}

/**
 * Slardar 日志上报
 * See: https://slardar.bytedance.net/docs/115/150/custom_log/
 * @param value
 * @param tags
 */
export function log(value: any, tags?: PlainObjectOf<string>) {
    const params: CustomLogParams = {
        value:
            typeof value === 'string'
                ? value
                : value instanceof Error
                ? value.toString()
                : JSON.stringify(value),
        tags: tags || {}
    };

    extendSlardarParamsWithAppInfo(params)
        .then((params) => {
            if (isDev) return console.log('[Slardar Log]: ', params);

            window.Slardar && window.Slardar('emit', 'log', params);
        })
        .catch(() => {});
}

/**
 * Slardar counter
 * @param eventName
 * @param options
 * @param options.inc
 * @param options.tags
 */
export function counter(
    eventName: string,
    options?: { inc?: number; tags?: PlainObjectOf<string> }
) {
    const { inc = 1, tags = {} } = options || {};

    event(
        eventName,
        {
            value: inc
        },
        tags
    );
}
